import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatChipInputEvent } from '@angular/material/chips';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldItem } from '@ukam/core/filters/models/filter-field-item';

interface GuidFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const GUID_FILTER_OPERATORS: GuidFilterOperatorOption[] = [
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
  { value: 'in', label: 'In' },
  { value: 'nin', label: 'Not in' },
];

@Component({
  selector: 'app-guid-filter',
  templateUrl: './guid-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppGuidFilterComponent), multi: true },
  ],
})
export class AppGuidFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = GUID_FILTER_OPERATORS;
  separatorKeys = [ENTER, COMMA];

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value = '';
  items: FilterFieldItem[] = [];

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(400)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isList(): boolean {
    return this.operator === 'in' || this.operator === 'nin';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    this.operator = op;
    this.value = '';
    this.items = [];
    this.emit({ operator: op, value: undefined, items: [] });
  }

  onValueChange(value: string): void {
    this.value = value;
    this.emit({ value: value || undefined });
  }

  onChipInputTokenEnd(event: MatChipInputEvent): void {
    this.addItem(event.value);
    event.chipInput?.clear();
  }

  removeItem(index: number): void {
    this.items = this.items.filter((_, i) => i !== index);
    this.emit({ items: this.items });
  }

  clear(): void {
    this.value = '';
    this.items = [];
    this.emit({ value: undefined, items: [] });
  }

  private addItem(raw: string): void {
    const trimmed = raw.trim();
    if (!trimmed || this.items.some(i => i.id === trimmed)) return;

    // GuidFilter's In/Nin read FilterFieldItem.Id (not Code) — see GetStringIds() in FilterField.cs.
    this.items = [...this.items, { id: trimmed, name: trimmed, selected: true }];
    this.emit({ items: this.items });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.value = this.field?.value ?? '';
    this.items = this.field?.items ?? [];
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
